import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { updateQueueMember } from 'flow/actions/pbx-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import useProfiles from 'flow/hooks/useProfiles';

import { ExtensionKey, ProfileKey, QueueKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { generatePath } from 'flow/utils/router-utils';
import { fromJS } from 'immutable';
import { useHistory, useParams } from 'react-router-dom';
import Toggle from '../ui-components/Toggle';
import Page from './Page';
import SettingRow from './SettingRow';
import Queue from 'flow/types/Queue';
import { LocationType } from 'flow/types/Location';
import { selectExtensionActiveProfileKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectAllQueueMembers,
    selectQueues,
} from 'flow/selectors/queue-selectors';

const QueueAndChatLoginPerProfile = () => {
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { extensionKey, queueKey } = useParams<{
        extensionKey: ExtensionKey;
        queueKey: QueueKey;
    }>();

    const isAiCoworker = window.location.pathname.includes('ai-coworker');
    const queues = useAppSelector(selectQueues);
    const allQueueMembers = useAppSelector(selectAllQueueMembers);
    const aiQueues = queues?.filter((queue: TypedMap<Queue>) =>
        allQueueMembers
            ?.get(queue.get('key'))
            ?.some((member) => member.get('extensionKey') === extensionKey),
    );
    const myQueues = isAiCoworker ? aiQueues : queues;

    const { profiles, getProfile } = useProfiles(extensionKey);
    const extensionActiveProfileKey = useAppSelector((state) =>
        selectExtensionActiveProfileKey(state, extensionKey),
    );

    if (!myQueues) {
        return null;
    }

    const getLoggedInValue = (profile: TypedMap<Profile>, queueKey: string) => {
        const queueLogin = profile
            .get('queueLoginList')
            .find(
                (findQueueLogin) =>
                    findQueueLogin.get('queueEntityKey') === queueKey,
            );
        if (!queueLogin) {
            return false;
        }
        return queueLogin.get('loggedIn', false);
    };

    const toggledProfileKeys = profiles
        .filter((profile) => getLoggedInValue(profile, queueKey))
        .map((profile) => profile.get('key'));

    const handleProfileToggle = (profileKey: ProfileKey, newValue: boolean) => {
        const profileDTO = getProfile(profileKey);
        const queueLoginList = profileDTO.get('queueLoginList');
        const indexOfQueueLogin = queueLoginList.findIndex(
            (queueLogin) => queueLogin.get('queueEntityKey') === queueKey,
        );
        // If the QueueLoginDTO is missing, we attempt to create a new one
        const updatedQueueLogin = (() => {
            if (indexOfQueueLogin === -1) {
                return queueLoginList.push({
                    loggedIn: newValue,
                    queueEntityKey: queueKey,
                });
            }

            return queueLoginList.update(indexOfQueueLogin, (queue: any) =>
                queue.set('loggedIn', newValue),
            );
        })();
        const updatedProfileDTO = fromJS({
            key: profileKey,
            queueLoginList: updatedQueueLogin,
        });
        dispatch(updateProfile(extensionKey, updatedProfileDTO));
        if (extensionActiveProfileKey === profileKey) {
            const memberDTO = allQueueMembers
                .get(queueKey)
                ?.find((member) => member.get('extensionKey') === extensionKey);
            if (memberDTO) {
                const updatedMemberDTO = memberDTO.set('loggedIn', newValue);
                dispatch(updateQueueMember(queueKey, updatedMemberDTO));
            }
        }
    };

    const title =
        !profiles.isEmpty() &&
        profiles
            .first()
            .get('queueLoginList')
            .find((queue) => queue.get('queueEntityKey') === queueKey)
            .get('queueDescription');
    return (
        <Page
            header={title}
            onClickBack={() => {
                history.push(
                    history.location.state?.from
                        ? history.location.state.from
                        : generatePath(
                              routes['settings.queue-and-chat-login'],
                              {
                                  extensionKey,
                              },
                          ),
                );
            }}
        >
            {profiles?.map((profile) => (
                <SettingRow
                    className="hover:bg-transparent"
                    key={profile.get('key')}
                    cta={
                        <Toggle
                            value={toggledProfileKeys.includes(
                                profile.get('key'),
                            )}
                            onChange={(value) =>
                                handleProfileToggle(profile.get('key'), value)
                            }
                            text={profile.get('description')}
                        />
                    }
                >
                    {profile.get('description')}
                </SettingRow>
            ))}
        </Page>
    );
};

export default QueueAndChatLoginPerProfile;
